import { ComponentType, useEffect } from "react"
import { ErrorBoundary, useErrorBoundary } from "react-error-boundary"
import { useTranslation } from "react-i18next"
import { create } from "zustand"
import { toast } from "@/hooks/useToast"

type State = {
  /** This field is used to retry request when network error */
  fetchKeys: Map<string, number>
  errorKeys: Map<string, boolean | undefined>
  updateFetchKey: (key: string) => void
  updateErrorKey: (key: string, error?: boolean) => void
}

const useFetchKeysStore = create<State>((set, get) => ({
  fetchKeys: new Map(),
  errorKeys: new Map(),

  updateFetchKey(key: string) {
    const fetchKeys = new Map(get().fetchKeys)
    fetchKeys.set(key, (fetchKeys.get(key) ?? 0) + 1)

    set({ fetchKeys })
  },

  updateErrorKey(key, error) {
    const errorKeys = new Map(get().errorKeys)
    errorKeys.set(key, error)
    set({ errorKeys })
  },
}))

const FallbackComponent = (props: { name: string }) => {
  const { t } = useTranslation()
  const { resetBoundary } = useErrorBoundary()
  const updateErrorKey = useFetchKeysStore((store) => store.updateErrorKey)

  useEffect(() => {
    updateErrorKey(props.name, true)
    return () => {
      resetBoundary()
    }
  }, [props.name, resetBoundary, updateErrorKey])

  useEffect(() => {
    toast({ description: t("common.somethingWentWrong") })
  }, [t])

  return <div></div>
}

FallbackComponent.displayName = "FallbackComponent"

const withErrorBoundary = <T,>(Component: ComponentType<T>, key: string) => {
  const InternalComponent = (props: T) => {
    const error = useFetchKeysStore((store) => store.errorKeys.get(key))
    const fetchKey = useFetchKeysStore((store) => store.fetchKeys.get(key) ?? 0)
    const updateFetchKey = useFetchKeysStore((store) => store.updateFetchKey)
    const updateErrorKey = useFetchKeysStore((store) => store.updateErrorKey)

    useEffect(() => {
      return () => {
        // reset error and update fetch key when component completed unmount
        updateErrorKey(key)
        updateFetchKey(key)
      }
    }, [updateErrorKey, updateFetchKey])

    return (
      <ErrorBoundary fallback={<FallbackComponent name={key} />}>
        {error ? null : <Component {...props} fetchKey={fetchKey} />}
      </ErrorBoundary>
    )
  }

  return InternalComponent
}

export { withErrorBoundary }
